import React from 'react';
import { useSelector } from 'react-redux';
import { Col, Row } from 'react-grid-system';
import Divider from '@Components/Divider/Divider';
import Accordion from '@Components/Accordion/Accordion';
import ConditionalRender from '@Components/ConditionalRender/ConditionalRender';
import useTranslate from '@Utils/hooks/useTranslate';
import { ICheckout } from '@Redux/modules/checkout/actions';
import { ICartItem, IInsuranceCartItem } from '@ApiModels/viewCart';
import LabeledInformation from './LabeledInformation';
import { getcontactDetailsArgs, getHbbWfbbContactDetailsArgs } from '../util';
import {
	AccordionContentWrapper,
	SectionHeader,
	DividerWrapper,
} from '@Components/Card/cards/ShippingDetails/ShippingDetails.styled';
import MapDetails from './MapDetails';
import { IAddress } from '@ApiModels/commerceProfile';

interface IContactPersonalDetails {
	contactDetails: ICheckout['payload']['contactDetails'];
	cartItemsLength: number;
	hbbWfbbItem: ICartItem | IInsuranceCartItem | undefined;
	orderIncludesPlan: boolean;
	orderWithPrepaid: boolean;
	otherDeviceIncart: boolean;
	showMap: boolean;
	selectedAddress: IAddress | undefined;
}

const ContactPersonalDetails = ({
	contactDetails,
	cartItemsLength,
	hbbWfbbItem,
	orderIncludesPlan,
	orderWithPrepaid,
	otherDeviceIncart,
	showMap,
	selectedAddress,
}: IContactPersonalDetails): JSX.Element => {
	const { translate } = useTranslate();
	const hbbWfbbProcessCheckout = useSelector(
		(state: types.redux.IState) => state.persistentCheckout.hbbWfbbProcessCheckout
	);
	const personTitle = (title: string): string => {
		if (title === '1') {
			return translate('contact.details.dropdown.mr');
		} else {
			return translate('contact.details.dropdown.ms');
		}
	};

	const getHbbWfbbContactDetailsSubtitle = () => {
		const args = getHbbWfbbContactDetailsArgs(hbbWfbbProcessCheckout);
		return translate(`shipping-details.accordion.contact-data.subtitle-${args.length}`, ...args);
	};

	const getContactDetailsSubtitle = (contactDetails: ICheckout['payload']['contactDetails']) => {
		const args = getcontactDetailsArgs(contactDetails);
		return translate(`shipping-details.accordion.contact-data.subtitle-${args.length}`, ...args);
	};

	return (
		<>
			<Accordion
				subtitleColor={orderWithPrepaid ? 'black54' : 'black87'}
				isPrepaid={orderWithPrepaid}
				title={translate(
					hbbWfbbItem || (orderWithPrepaid && !otherDeviceIncart)
						? 'shipping-details.accordion.contact-data.personal.info'
						: orderIncludesPlan
						? 'shipping-details.accordion.contact-data.title'
						: 'shipping-details.accordion.contact-data.title.without.billing'
				)}
				subtitle={hbbWfbbItem ? getHbbWfbbContactDetailsSubtitle() : getContactDetailsSubtitle(contactDetails)}
				icon="person"
				accordionHeight={80}
			>
				<AccordionContentWrapper>
					<SectionHeader>{translate('shipping-details.accordion.contact-data.section-header')}</SectionHeader>
					<Divider marginBottom={8} />
					<ConditionalRender
						show={!!hbbWfbbItem}
						onTrue={() => (
							<>
								<ConditionalRender
									show={!!hbbWfbbProcessCheckout?.title}
									onTrue={() => (
										<LabeledInformation
											label={translate('shipping-details.accordion.contact-data.data.title')}
											data={personTitle(String(hbbWfbbProcessCheckout?.title))}
										/>
									)}
								/>
								<ConditionalRender
									show={!!hbbWfbbProcessCheckout?.firstName && !!hbbWfbbProcessCheckout?.lastName}
									onTrue={() => (
										<LabeledInformation
											label={translate('shipping-details.accordion.contact-data.data.name')}
											data={`${String(hbbWfbbProcessCheckout?.firstName)} ${String(
												hbbWfbbProcessCheckout?.lastName
											)}`}
										/>
									)}
								/>
								<ConditionalRender
									show={!!hbbWfbbProcessCheckout?.emailId}
									onTrue={() => (
										<LabeledInformation
											label={translate('shipping-details.accordion.contact-data.data.email')}
											data={String(hbbWfbbProcessCheckout?.emailId)}
										/>
									)}
								/>
								<ConditionalRender
									show={!!hbbWfbbProcessCheckout?.contactNumber1}
									onTrue={() => (
										<LabeledInformation
											label={translate(
												'shipping-details.accordion.contact-data.data.phoneNumber'
											)}
											data={hbbWfbbProcessCheckout?.contactNumber1}
										/>
									)}
								/>
							</>
						)}
						onFalse={() => {
							return (
								<ConditionalRender
									show={!!cartItemsLength}
									onTrue={() => (
										<>
											<ConditionalRender
												show={!!contactDetails?.title}
												onTrue={() => (
													<LabeledInformation
														label={translate(
															'shipping-details.accordion.contact-data.data.title'
														)}
														data={personTitle(String(contactDetails?.title))}
													/>
												)}
											/>
											<ConditionalRender
												show={!!contactDetails?.firstName && !!contactDetails?.lastName}
												onTrue={() => (
													<LabeledInformation
														label={translate(
															'shipping-details.accordion.contact-data.data.name'
														)}
														data={`${String(contactDetails?.firstName)} ${String(
															contactDetails?.lastName
														)}`}
													/>
												)}
											/>
											<ConditionalRender
												show={!!contactDetails?.email}
												onTrue={() => (
													<LabeledInformation
														label={translate(
															'shipping-details.accordion.contact-data.data.email'
														)}
														data={String(contactDetails?.email)}
													/>
												)}
											/>
											<ConditionalRender
												show={!!contactDetails?.phoneNumber}
												onTrue={() => (
													<LabeledInformation
														label={translate(
															'shipping-details.accordion.contact-data.data.phoneNumber'
														)}
														data={contactDetails?.phoneNumber}
													/>
												)}
											/>
										</>
									)}
								/>
							);
						}}
					/>
					<ConditionalRender
						show={orderIncludesPlan && !orderWithPrepaid}
						onTrue={() => (
							<>
								<Divider withoutLine marginTop={40} />
								<SectionHeader>
									{translate('shipping-details.accordion.billing-data.section-header')}
								</SectionHeader>
								<Divider marginBottom={8} />
								<Row>
									<Col xs={12} sm={6}>
										<ConditionalRender
											show={!!selectedAddress?.governorate}
											onTrue={() => (
												<LabeledInformation
													label={translate(
														'shipping-details.billing.contact-data.data.governorate'
													)}
													data={selectedAddress?.governorate}
												/>
											)}
										/>
										<ConditionalRender
											show={!!selectedAddress?.wilayat}
											onTrue={() => (
												<LabeledInformation
													label={translate(
														'shipping-details.billing.contact-data.data.wilayat'
													)}
													data={selectedAddress?.wilayat}
												/>
											)}
										/>
										<ConditionalRender
											show={!!selectedAddress?.city}
											onTrue={() => (
												<LabeledInformation
													label={
														!!selectedAddress?.area
															? translate(
																	'shipping-details.billing.contact-data.data.city-area'
															  )
															: translate(
																	'shipping-details.billing.contact-data.data.city'
															  )
													}
													data={
														!!selectedAddress?.area
															? `${selectedAddress?.city} / ${selectedAddress?.area}`
															: selectedAddress?.city
													}
												/>
											)}
										/>
										<ConditionalRender
											show={!!selectedAddress?.way}
											onTrue={() => (
												<LabeledInformation
													label={translate('shipping-details.billing.contact-data.data.way')}
													data={selectedAddress?.way}
												/>
											)}
										/>
										<ConditionalRender
											show={!!selectedAddress?.house}
											onTrue={() => (
												<LabeledInformation
													label={translate('deliver-address.text-field.house-info')}
													data={
														selectedAddress?.house ??
														'' +
															(selectedAddress?.house && selectedAddress?.apartment
																? '/' + selectedAddress.apartment
																: '')
													}
												/>
											)}
										/>
									</Col>

									<MapDetails selectedAddress={selectedAddress} showMap={showMap} />
								</Row>
							</>
						)}
					/>
				</AccordionContentWrapper>
			</Accordion>
			<DividerWrapper>
				<Divider />
			</DividerWrapper>
		</>
	);
};

export default ContactPersonalDetails;
